import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { SEASONS } from '../../utils/constants';
import { LifeStats } from '../../hooks/useLifeStats';
import Card from '../Card';
import ProgressBar from '../ProgressBar';
import { springConfig, staggerContainer } from '../../utils/animations';

interface SeasonsViewProps {
  stats: LifeStats;
}

const SeasonsView: React.FC<SeasonsViewProps> = ({ stats }) => {
  const prefersReducedMotion = useReducedMotion();

  if (stats.lifeExpectancy <= 0) return null;

  // Season card animation variants
  const seasonCardVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.9 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        ...springConfig.bouncy,
        delay: i * 0.15
      }
    }),
    hover: {
      y: -8,
      scale: 1.02,
      transition: springConfig.snappy
    }
  };

  // If reduced motion is preferred, render without animations
  if (prefersReducedMotion) {
    return (
      <div className="w-full max-w-4xl">
        <div className="mb-6">
          <ProgressBar
            value={stats.yearsLived}
            maxValue={stats.lifeExpectancy}
            label="Life Journey"
            height="h-6"
            animate={false}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {SEASONS.map((season) => {
            const inSeason = stats.currentYear >= season.start && stats.currentYear <= season.end;
            const seasonYears = season.end - season.start + 1;

            let yearsInSeason = 0;
            if (stats.yearsLived > season.start) {
              yearsInSeason = Math.min(stats.yearsLived - season.start, seasonYears);
            }

            const SeasonIcon = season.icon;

            return (
              <Card
                key={season.name}
                className={`p-4 ${inSeason ? 'border-2 border-' + season.color + '-500 dark:border-catppuccin-' + season.color : ''}`}
                animated={false}
              >
                <div className={`text-${season.color}-500 dark:text-catppuccin-${season.color} flex items-center gap-2 mb-3`}>
                  <SeasonIcon size={24} />
                  <h3 className="text-lg font-bold">{season.name}</h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-catppuccin-subtext0 mb-4">{season.description}</p>
                <div className="text-sm mb-2 dark:text-catppuccin-text">
                  Years {season.start + 1} - {season.end + 1}
                </div>
                <ProgressBar
                  value={yearsInSeason}
                  maxValue={seasonYears}
                  colorClass={`bg-${season.color}-500`}
                  height="h-3"
                  animate={false}
                />
                <div className="mt-4 text-sm text-gray-500 dark:text-catppuccin-subtext0">
                  {yearsInSeason.toFixed(1)} of {seasonYears} years
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl">
      <motion.div
        className="mb-6"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <ProgressBar
          value={stats.yearsLived}
          maxValue={stats.lifeExpectancy}
          label="Life Journey"
          height="h-6"
          animate={true}
        />
      </motion.div>

      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
        variants={staggerContainer(0.15, 0.3)}
        initial="hidden"
        animate="visible"
      >
        {SEASONS.map((season, index) => {
          const inSeason = stats.currentYear >= season.start && stats.currentYear <= season.end;
          const seasonYears = season.end - season.start + 1;

          let yearsInSeason = 0;
          if (stats.yearsLived > season.start) {
            yearsInSeason = Math.min(stats.yearsLived - season.start, seasonYears);
          }

          const SeasonIcon = season.icon;

          return (
            <motion.div
              key={season.name}
              variants={seasonCardVariants}
              custom={index}
              whileHover="hover"
            >
              <motion.div
                animate={inSeason ? {
                  boxShadow: [
                    '0 0 0 0 rgba(59, 130, 246, 0)',
                    '0 0 20px 4px rgba(59, 130, 246, 0.3)',
                    '0 0 0 0 rgba(59, 130, 246, 0)'
                  ],
                } : undefined}
                transition={inSeason ? {
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut'
                } : undefined}
              >
                <Card
                  className={`p-4 h-full ${inSeason ? 'border-2 border-' + season.color + '-500 dark:border-catppuccin-' + season.color : ''}`}
                  animated={false}
                  hoverEffect={false}
                >
                  <motion.div
                    className={`text-${season.color}-500 dark:text-catppuccin-${season.color} flex items-center gap-2 mb-3`}
                  >
                    <motion.div
                      initial={{ rotate: 0, scale: 1 }}
                      whileHover={{ rotate: [0, -15, 15, 0], scale: 1.2, transition: { duration: 0.5 } }}
                      animate={inSeason ? { scale: [1, 1.3, 1] } : { rotate: 0, scale: 1 }}
                      transition={inSeason ? { duration: 2, repeat: Infinity, ease: 'easeInOut' } : undefined}
                    >
                      <SeasonIcon size={24} />
                    </motion.div>
                    <h3 className="text-lg font-bold">{season.name}</h3>
                    {inSeason && (
                      <motion.span
                        className="ml-auto text-xs bg-blue-100 dark:bg-catppuccin-surface1 text-blue-600 dark:text-catppuccin-blue px-2 py-1 rounded-full"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={springConfig.bouncy}
                      >
                        Current
                      </motion.span>
                    )}
                  </motion.div>
                  <p className="text-sm text-gray-600 dark:text-catppuccin-subtext0 mb-4">{season.description}</p>
                  <div className="text-sm mb-2 dark:text-catppuccin-text">
                    Years {season.start + 1} - {season.end + 1}
                  </div>
                  <ProgressBar
                    value={yearsInSeason}
                    maxValue={seasonYears}
                    colorClass={`bg-${season.color}-500`}
                    height="h-3"
                    animate={true}
                  />
                  <motion.div
                    className="mt-4 text-sm text-gray-500 dark:text-catppuccin-subtext0"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                  >
                    {yearsInSeason.toFixed(1)} of {seasonYears} years
                  </motion.div>
                </Card>
              </motion.div>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
};

export default SeasonsView;
